import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Edit2, Trash2, RefreshCw, RotateCcw, Ticket, X, ChevronDown, ChevronUp } from 'lucide-react';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px',
    background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer',
    fontSize: 13, color: color || 'var(--text-secondary)', transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const UserMenuButton = ({ u, openMenuId, setOpenMenuId, onlyDeleted, syncingId, renewingId, handleRestore, openEdit, handleSync, handleRenew, setDeleteConfirm, setDeletePermanent, setAttrsModal, openTest, isSuperadmin }) => {
  const btnRef = React.useRef(null);
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === u.id ? null : u.id); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>
        ···
      </button>
      {openMenuId === u.id && ReactDOM.createPortal(
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute',
          top: (() => { const r = btnRef.current?.getBoundingClientRect(); return r ? r.bottom + window.scrollY + 4 : 0; })(),
          left: (() => { const r = btnRef.current?.getBoundingClientRect(); return r ? r.right + window.scrollX : 0; })(),
          transform: 'translateX(-100%)', zIndex: 9999,
          background: 'var(--bg-secondary)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 170, overflow: 'hidden',
        }}>
          {onlyDeleted ? (
            <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(u); setOpenMenuId(null); }}>↩ Restore</MenuItem>
          ) : (<>
            <MenuItem onClick={() => { openEdit(u); setOpenMenuId(null); }}>✏️ Edit</MenuItem>
            <MenuItem loading={syncingId === u.id} onClick={() => { handleSync(u); setOpenMenuId(null); }}>🔄 Sync</MenuItem>
            <MenuItem onClick={() => { openTest(u); setOpenMenuId(null); }}>🧪 Test</MenuItem>
            {u.status !== 'pre_active' && u.status !== 'suspended' && (
              <MenuItem loading={renewingId === u.id} onClick={() => { handleRenew(u); setOpenMenuId(null); }}>⏱ Renew Expired</MenuItem>
            )}
          </>)}
          {(!onlyDeleted || isSuperadmin) && (<>
            <div style={{ borderTop: '1px solid var(--border)' }} />
            <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(u); setDeletePermanent(false); setOpenMenuId(null); }}>🗑 Hapus</MenuItem>
          </>)}
        </div>,
        document.body
      )}
    </div>
  );
};
import { usersAPI, tenantsAPI, profilesAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, Modal, Input, Select, SearchSelect, PageHeader, Alert, useSort } from '../components/UI';
import DurationInput from '../components/DurationInput';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';

const EMPTY_FORM = {
  type: 'regular', username: '', password: '', email: '', phone: '',
  fullname: '', status: '', profile_id: '', ip_assignment: 'nas',
  static_ip: '', expired_at: '', service_type: '', shared_user: '', custom_attrs: [],
};

const EMPTY_ATTR = { attribute: '', op: ':=', value: '', type: 'reply' };


const StatusBadge = ({ status }) => {
  const map = {
    active:     { cls: 'badge-active'    },
    inactive:   { cls: 'badge-inactive'  },
    suspended:  { cls: 'badge-suspended' },
    expired:    { cls: 'badge-expired'   },
    pre_active: { cls: 'badge-preactive' },
  };
  const m = map[status];
  if (!m) return <span className="badge badge-inactive">{status || '—'}</span>;
  return <span className={`badge ${m.cls}`}>{status}</span>;
};

const SyncBadge = ({ status }) => {
  const map = {
    synced:  { color: 'var(--accent-green)',  bg: 'rgba(16,185,129,0.12)'  },
    pending: { color: 'var(--accent-orange)', bg: 'rgba(245,158,11,0.12)'  },
    error:   { color: 'var(--accent-red)',    bg: 'rgba(239,68,68,0.12)'   },
  };
  const s = map[status] || { color: 'var(--text-muted)', bg: 'var(--bg-input)' };
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44`, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{status || '—'}</span>;
};



const VOUCHER_FORM = { profile_id: '', count: 10, prefix: 'VCH', separator: '-', length: 8, char_type: 'alphanumeric', letter_case: 'upper', increment: false, status: 'pre_active', custom_attrs: [] };

export default function Users() {
  const [searchParams] = useSearchParams();
  const { tenants, selectedTenant } = useTenant();
  const { user: me } = useAuth();
  const isSuperadmin = me?.role === 'superadmin';
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [voucherModal, setVoucherModal] = useState(false);
  const [voucherForm, setVoucherForm] = useState(VOUCHER_FORM);
  const [voucherSaving, setVoucherSaving] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [renewingId, setRenewingId] = useState(null);
  const [attrsModal, setAttrsModal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const [testModal, setTestModal] = useState(null);       // user row lagi di-test
  const [testPassword, setTestPassword] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkRenewing, setBulkRenewing] = useState(false);
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [bulkUpdateForm, setBulkUpdateForm] = useState({ status: '', profile_id: '', ip_assignment: '', static_ip: '' });
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    if (!selectedTenant) { setProfiles([]); return; }
    profilesAPI.list(selectedTenant).then(r => setProfiles(r.profiles || [])).catch(() => {});
  }, [selectedTenant]);

  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    if (!selectedTenant) { setUsers([]); setTotal(0); return; }
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      if (serviceTypeFilter) params.service_type = serviceTypeFilter;
      if (statusFilter) params.status = statusFilter; // bisa comma-separated: active,expired
      if (onlyDeleted) params.only_deleted = true;
      if (sortParam) params.sort = sortParam;
      const res = await usersAPI.list(selectedTenant, params);
      setUsers(res.users || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedTenant, page, q, statusFilter, serviceTypeFilter, onlyDeleted, perPage, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [selectedTenant, onlyDeleted]);

  const openCreate = () => {
    if (!selectedTenant) { setError('Pilih tenant terlebih dahulu.'); return; }
    setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setModalOpen(true);
  };
  // close dropdown on outside click
  React.useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  const toLocalDatetime = (iso) => {
    if (!iso) return '';
    // datetime-local butuh format "YYYY-MM-DDTHH:mm"
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setForm({
      ...u,
      password: '',
      profile_id: u.profile_id || '',
      expired_at: toLocalDatetime(u.expired_at),
      service_type: u.service_type || '',
      shared_user: u.shared_user ?? '',
      custom_attrs: Array.isArray(u.custom_attrs)
        ? u.custom_attrs.map(a => ({ attribute: a.attribute || '', op: a.op || ':=', value: a.value || '', type: a.type || 'reply' }))
        : [],
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (!payload.profile_id) delete payload.profile_id;
      if (!payload.static_ip) delete payload.static_ip;
      if (!payload.expired_at) delete payload.expired_at;
      if (!payload.service_type) delete payload.service_type;
      if (!payload.status) delete payload.status;
      if (payload.shared_user === '') delete payload.shared_user;
      else if (payload.shared_user !== undefined) payload.shared_user = Number(payload.shared_user);
      // password kosong saat edit = tidak mengubah password
      if (editTarget && !payload.password) delete payload.password;
      payload.custom_attrs = Array.isArray(form.custom_attrs) ? form.custom_attrs : [];
      if (editTarget) await usersAPI.update(selectedTenant, editTarget.id, payload);
      else await usersAPI.create(selectedTenant, payload);
      setModalOpen(false);
      setSuccess(editTarget ? 'User diperbarui.' : 'User dibuat.');
      load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await usersAPI.deletePermanent(selectedTenant, deleteConfirm.id);
      else await usersAPI.delete(selectedTenant, deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? `User dihapus permanen.` : `User "${deleteConfirm.username}" dihapus.`);
      load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (u) => {
    try { await usersAPI.restore(selectedTenant, u.id); setSuccess('User dipulihkan.'); load(); }
    catch (e) { setError(e.message); }
  };

  const handleSync = async (u) => {
    setSyncingId(u.id);
    try { await usersAPI.sync(selectedTenant, u.id); setSuccess('User di-sync.'); load(); }
    catch (e) { setError(e.message); }
    finally { setSyncingId(null); }
  };

  const handleRenew = async (u) => {
    setRenewingId(u.id);
    try { await usersAPI.renew(selectedTenant, u.id, { from_now: true }); setSuccess('Expired diperpanjang.'); load(); }
    catch (e) { setError(e.message); }
    finally { setRenewingId(null); }
  };

  const handleGenerateVouchers = async () => {
    setVoucherSaving(true);
    try {
      const res = await usersAPI.generateVouchers(selectedTenant, voucherForm);
      setVoucherModal(false);
      // res bisa berupa { users: [...], total } atau { generated: N } atau array
      const count = res?.generated ?? res?.total ?? res?.users?.length ?? voucherForm.count;
      setSuccess(`${count} voucher berhasil dibuat.`);
      load();
    } catch (e) { setError(e.message); }
    finally { setVoucherSaving(false); }
  };

  // ── Test — kirim Access-Request langsung ke FreeRADIUS pakai username+password user ─
  const openTest = (u) => {
    setTestModal(u);
    setTestPassword('');
    setTestResult(null);
    setTestError(null);
  };

  const runTest = async () => {
    if (!testModal || !testPassword) return;
    setTestLoading(true); setTestError(null); setTestResult(null);
    try {
      const res = await usersAPI.test(selectedTenant, testModal.id, { password: testPassword });
      setTestResult(res);
    } catch (e) {
      setTestError(e.message);
    } finally {
      setTestLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => usersAPI.deletePermanent(selectedTenant, id)));
      else await Promise.all(selected.map(id => usersAPI.delete(selectedTenant, id)));
      setSuccess(`${selected.length} user ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selected.map(id => usersAPI.restore(selectedTenant, id)));
      setSuccess(`${selected.length} user dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try {
      await Promise.all(selected.map(id => usersAPI.sync(selectedTenant, id)));
      setSuccess(`${selected.length} user di-sync.`);
      setSelected([]); load();
    } catch (e) { setError(e.message); }
    finally { setBulkSyncing(false); }
  };

  const handleBulkRenew = async () => {
    setBulkRenewing(true);
    try {
      await Promise.all(selected.map(id => usersAPI.renew(selectedTenant, id, { from_now: true })));
      setSuccess(`${selected.length} user diperpanjang.`);
      setSelected([]); load();
    } catch (e) { setError(e.message); }
    finally { setBulkRenewing(false); }
  };

  const handleBulkUpdate = async () => {
    setBulkUpdating(true);
    try {
      const payload = {};
      if (bulkUpdateForm.status)       payload.status       = bulkUpdateForm.status;
      if (bulkUpdateForm.profile_id)   payload.profile_id   = bulkUpdateForm.profile_id;
      if (bulkUpdateForm.ip_assignment)payload.ip_assignment= bulkUpdateForm.ip_assignment;
      if (bulkUpdateForm.static_ip)    payload.static_ip    = bulkUpdateForm.static_ip;
      if (Object.keys(payload).length === 0) { setError('Pilih minimal satu field untuk diupdate.'); setBulkUpdating(false); return; }
      await Promise.all(selected.map(id => usersAPI.update(selectedTenant, id, payload)));
      setSuccess(`${selected.length} user diperbarui.`);
      setSelected([]); setBulkUpdateModal(false);
      setBulkUpdateForm({ status: '', profile_id: '', ip_assignment: '', static_ip: '' });
      load();
    } catch (e) { setError(e.message); }
    finally { setBulkUpdating(false); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const vf = (k, v) => setVoucherForm(p => ({ ...p, [k]: v }));
  const addVoucherAttr    = () => setVoucherForm(p => ({ ...p, custom_attrs: [...p.custom_attrs, { ...EMPTY_ATTR }] }));
  const removeVoucherAttr = (i) => setVoucherForm(p => ({ ...p, custom_attrs: p.custom_attrs.filter((_, idx) => idx !== i) }));
  const updateVoucherAttr = (i, key, val) => setVoucherForm(p => ({ ...p, custom_attrs: p.custom_attrs.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));
  const addAttr    = () => setForm(p => ({ ...p, custom_attrs: [...p.custom_attrs, { ...EMPTY_ATTR }] }));
  const removeAttr = (i) => setForm(p => ({ ...p, custom_attrs: p.custom_attrs.filter((_, idx) => idx !== i) }));
  const updateAttr = (i, key, val) => setForm(p => ({ ...p, custom_attrs: p.custom_attrs.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));
  const profileName = (id) => profiles.find(p => p.id === id)?.name || `#${id}`;

  const fmtDT = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return d.toLocaleString('id-ID', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', hour12: false });
  };

  const columns = [
    { key: 'username', label: 'Username', sortable: true, render: v => <span className="mono" style={{ color: 'var(--text-mono)', fontSize: 12 }}>{v}</span> },
    { key: 'fullname', label: 'Full Name', sortable: true, render: v => <span style={{ fontSize: 12 }}>{v || '—'}</span> },
    { key: 'type', label: 'Type', sortable: true, render: v => <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4, border: '1px solid var(--border)' }}>{v}</span> },
    { key: 'profile_id', label: 'Profile', render: v => v ? <span style={{ fontSize: 11, color: 'var(--accent-cyan)' }}>{profileName(v)}</span> : '—' },
    { key: 'status', label: 'Status', sortable: true, render: v => <StatusBadge status={v} /> },
    { key: 'service_type', label: 'Service', sortable: true, render: v => v ? <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4, border: '1px solid var(--border)' }}>{v}</span> : '—' },
    { key: 'shared_user', label: 'Shared', render: v => v !== null && v !== undefined ? <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v === 0 ? '∞' : v}</span> : '—' },
    { key: 'sync_status', label: 'Sync', render: v => <SyncBadge status={v} /> },
    { key: 'custom_attrs', label: 'Attrs', render: v => {
      const count = Array.isArray(v) ? v.length : 0;
      return (
        <span style={{ fontSize: 11, padding: '2px 8px', background: count > 0 ? 'rgba(139,92,246,0.15)' : 'var(--bg-input)', color: count > 0 ? 'var(--accent-purple)' : 'var(--text-muted)', borderRadius: 20, border: `1px solid ${count > 0 ? 'rgba(139,92,246,0.3)' : 'var(--border)'}` }}>
          {count}
        </span>
      );
    }},
    { key: 'activated_at', label: 'Activated', sortable: true, render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDT(v)}</span> },
    { key: 'expired_at', label: 'Expires', sortable: true, render: v => v ? <span style={{ fontSize: 11, color: new Date(v) < new Date() ? 'var(--accent-red)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDT(v)}</span> : '—' },
    {
      key: 'id', label: 'Actions', render: (_, u) => (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}>
            {expandedId === u.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </Button>
          <UserMenuButton u={u} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId}
            onlyDeleted={onlyDeleted} syncingId={syncingId} renewingId={renewingId}
            handleRestore={handleRestore} openEdit={openEdit} handleSync={handleSync}
            handleRenew={handleRenew} setDeleteConfirm={setDeleteConfirm}
            setDeletePermanent={setDeletePermanent} setAttrsModal={setAttrsModal}
            openTest={openTest} isSuperadmin={isSuperadmin} />
        </div>
      )
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="Users"
        subtitle={selectedTenant ? `${total} user di tenant ini` : 'Pilih tenant untuk melihat user'}
        actions={
          <div className="bulk-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (<>
              {!onlyDeleted && <>
                <Button variant="ghost" loading={bulkSyncing} onClick={handleBulkSync}><RefreshCw size={13} /> Sync {selected.length}</Button>
                <Button variant="ghost" loading={bulkRenewing} onClick={handleBulkRenew}><RotateCcw size={13} /> Renew {selected.length}</Button>
                <Button variant="secondary" onClick={() => setBulkUpdateModal(true)}><Edit2 size={13} /> Update {selected.length}</Button>
              </>}
              {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
              {(!onlyDeleted || isSuperadmin) && (
                <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}><Trash2 size={13} /> Hapus {selected.length}</Button>
              )}
            </>)}
            <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
              {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
            </Button>
            <Button variant="secondary" onClick={() => setVoucherModal(true)} disabled={!selectedTenant}><Ticket size={14} /> Generate Voucher</Button>
            <Button onClick={openCreate} disabled={!selectedTenant}><Plus size={14} /> Tambah User</Button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Filters */}
      <div className="filter-bar" style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Cari username / nama…"
          value={q}
          onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13, width: 200, outline: 'none', fontFamily: 'var(--font-sans)' }}
        />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer' }}>
          <option value="">Semua Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
          <option value="expired">Expired</option>
          <option value="pre_active">Pre Active</option>
        </select>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4 }}>
          {[['', 'All'], ['ppp', 'PPP'], ['hotspot', 'Hotspot'], ['device_login', 'Device']].map(([val, label]) => (
            <button key={val} onClick={() => { setServiceTypeFilter(val); setPage(1); }}
              style={{ padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                background: serviceTypeFilter === val ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: serviceTypeFilter === val ? 'var(--accent-blue)' : 'var(--text-muted)',
                fontWeight: serviceTypeFilter === val ? 600 : 400, transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={users} loading={loading} sort={sort} onSortChange={onSortChange} emptyMessage={selectedTenant ? 'Belum ada user' : 'Pilih tenant terlebih dahulu'} selected={selected} onSelect={setSelected}
          expandedId={expandedId}
          renderExpanded={u => {
            const attrs = Array.isArray(u.custom_attrs) ? u.custom_attrs : [];
            return (<>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 12 }}>RADIUS Attributes</div>
              {attrs.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tidak ada custom attribute</span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {attrs.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 10px', background: 'var(--bg-card)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12 }}>
                      <span style={{ color: 'var(--accent-orange)', minWidth: 80 }}>[{a.type}]</span>
                      <span className="mono" style={{ color: 'var(--text-primary)', flex: 1 }}>{a.attribute}</span>
                      <span style={{ color: 'var(--text-muted)', minWidth: 28 }}>{a.op}</span>
                      <span className="mono" style={{ color: 'var(--accent-cyan)', flex: 1 }}>{a.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {u.last_synced_at && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>Last synced: {new Date(u.last_synced_at).toLocaleString('id-ID')}{u.sync_error && <span style={{ color: 'var(--accent-red)', marginLeft: 8 }}>Error: {u.sync_error}</span>}</div>}
            </>);
          }}
        />
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} />
        </div>
      </Card>

      {/* User Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit User' : 'Tambah User'} width={620}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!editTarget && (
            <Select label="Tipe User" value={form.type} onChange={e => f('type', e.target.value)}>
              <option value="regular">Regular</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </Select>
          )}
          {(form.type === 'regular' || editTarget) && (
            <>
              <Input label="Username *" value={form.username} onChange={e => f('username', e.target.value)} disabled={editTarget && form.type !== 'regular'} />
              <Input label="Password" type="password" value={form.password} onChange={e => f('password', e.target.value)} placeholder={editTarget ? '(kosong = tidak berubah)' : ''} />
            </>
          )}
          {form.type === 'email' && !editTarget && <Input label="Email *" value={form.email} onChange={e => f('email', e.target.value)} />}
          {form.type === 'phone' && !editTarget && <Input label="Phone *" value={form.phone} onChange={e => f('phone', e.target.value)} />}
          <Input label="Full Name" value={form.fullname} onChange={e => f('fullname', e.target.value)} />
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 8 }}>Status</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(editTarget ? [
                ['active',     'Active',      'var(--accent-green)',  'rgba(16,185,129,0.1)'],
                ['suspended',  'Suspended',   'var(--accent-orange)', 'rgba(245,158,11,0.1)'],
                ['expired',    'Expired',     'var(--accent-red)',    'rgba(239,68,68,0.1)'],
                ['inactive',   'Inactive',    'var(--text-muted)',    'rgba(100,100,100,0.1)'],
              ] : [
                ['',           'Default (Pre-active)', 'var(--text-muted)', 'rgba(100,100,100,0.1)'],
                ['active',     'Active',      'var(--accent-green)',  'rgba(16,185,129,0.1)'],
              ]).map(([val, label, color, bg]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '6px 14px', borderRadius: 'var(--radius)', border: `1px solid ${form.status === val ? color : 'var(--border)'}`, background: form.status === val ? bg : 'var(--bg-input)', color: form.status === val ? color : 'var(--text-secondary)', transition: 'all 0.15s', fontWeight: form.status === val ? 600 : 400 }}>
                  <input type="radio" name="user_status" value={val} checked={form.status === val} onChange={() => f('status', val)} style={{ accentColor: 'currentColor' }} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <SearchSelect label="Profile" value={form.profile_id} onChange={id => f('profile_id', id || '')}
              options={profiles} getKey={p => p.id} getLabel={p => p.name}
              allowClear clearLabel="— Tanpa Profile —" placeholder="— Tanpa Profile —" defaultCount={10} />
            <Select label="IP Assignment" value={form.ip_assignment} onChange={e => f('ip_assignment', e.target.value)}>
              <option value="nas">NAS (dynamic)</option>
              <option value="static">Static IP</option>
            </Select>
            <Input label="Expired At" type="datetime-local" value={form.expired_at || ''} onChange={e => f('expired_at', e.target.value)} />
            <div style={{ gridColumn: '1/-1' }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 8 }}>Service Type</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[['', 'None'], ['ppp', 'PPP'], ['hotspot', 'Hotspot'], ['device_login', 'Device Login']].map(([val, label]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '5px 10px', borderRadius: 'var(--radius)', border: `1px solid ${form.service_type === val ? 'var(--accent-blue)' : 'var(--border)'}`, background: form.service_type === val ? 'rgba(59,130,246,0.1)' : 'var(--bg-input)', color: form.service_type === val ? 'var(--accent-blue)' : 'var(--text-secondary)', transition: 'all 0.15s' }}>
                    <input type="radio" name="user_service_type" value={val} checked={form.service_type === val} onChange={() => f('service_type', val)} style={{ accentColor: 'var(--accent-blue)' }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <Input label="Shared User (0=unlimited)" type="number" value={form.shared_user} onChange={e => f('shared_user', e.target.value)} placeholder="0" />
          </div>
          {form.ip_assignment === 'static' && <Input label="Static IP" value={form.static_ip} onChange={e => f('static_ip', e.target.value)} placeholder="192.168.1.100" />}

          {/* custom_attrs */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                RADIUS Attributes
                <span style={{ color: 'var(--accent-purple)', marginLeft: 6 }}>({form.custom_attrs.length})</span>
              </span>
              <Button size="sm" variant="ghost" onClick={addAttr}><Plus size={12} /> Tambah</Button>
            </div>
            {form.custom_attrs.length === 0 ? (
              <div style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px dashed var(--border)', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                Kosong — dikirim sebagai <span className="mono" style={{ color: 'var(--accent-purple)' }}>custom_attrs: []</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 58px 1fr 22px', gap: 6 }}>
                  {['Type','Attribute','Op','Value',''].map(h => (
                    <span key={h} style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{h}</span>
                  ))}
                </div>
                {form.custom_attrs.map((attr, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 58px 1fr 22px', gap: 6, alignItems: 'center' }}>
                    <select value={attr.type} onChange={e => updateAttr(i, 'type', e.target.value)}
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 6px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                      <option value="reply">reply</option>
                      <option value="check">check</option>
                    </select>
                    <input value={attr.attribute} onChange={e => updateAttr(i, 'attribute', e.target.value)}
                      placeholder="Mikrotik-Rate-Limit"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                    <select value={attr.op} onChange={e => updateAttr(i, 'op', e.target.value)}
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 4px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                      {[':=','=','+=','==','!=','>=','<='].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input value={attr.value} onChange={e => updateAttr(i, 'value', e.target.value)}
                      placeholder="10M/10M"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                    <button onClick={() => removeAttr(i)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Buat User'}</Button>
        </div>
      </Modal>

      {/* Test Modal — kirim Access-Request langsung ke FreeRADIUS pakai radtest */}
      <Modal open={!!testModal} onClose={() => setTestModal(null)} title={`Test Auth — ${testModal?.username}`} width={560}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Password" type="text" value={testPassword} onChange={e => setTestPassword(e.target.value)}
            placeholder="Password user" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={runTest} loading={testLoading} disabled={!testPassword}>Jalankan Test</Button>
          </div>

          {testError && <Alert type="error" message={testError} />}

          {testResult && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Badge status={testResult.success ? 'active' : 'suspended'} />
                <span style={{ fontSize: 13, fontWeight: 600, color: testResult.success ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {testResult.success ? 'Access-Accept' : 'Access-Reject'}
                </span>
              </div>
              <pre className="mono" style={{
                background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: 12, fontSize: 11, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                maxHeight: 320, overflow: 'auto', color: 'var(--text-secondary)',
              }}>{testResult.output}</pre>
            </div>
          )}
        </div>
      </Modal>

      {/* Voucher Modal */}
      <Modal open={voucherModal} onClose={() => setVoucherModal(false)} title="Generate Vouchers" width={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SearchSelect label="Profile *" value={voucherForm.profile_id} onChange={id => vf('profile_id', id || '')}
            options={profiles} getKey={p => p.id} getLabel={p => p.name}
            placeholder="— Pilih Profile —" defaultCount={10} />
          <Input label="Jumlah Voucher (1–500)" type="number" min={1} max={500} value={voucherForm.count} onChange={e => vf('count', +e.target.value)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Prefix" value={voucherForm.prefix} onChange={e => vf('prefix', e.target.value)} placeholder="VCH" />
            <Input label="Separator" value={voucherForm.separator} onChange={e => vf('separator', e.target.value)} placeholder="-" />
            <Input label="Panjang Kode" type="number" min={4} max={32} value={voucherForm.length} onChange={e => vf('length', +e.target.value)} />
            <Select label="Tipe Karakter" value={voucherForm.char_type} onChange={e => vf('char_type', e.target.value)}>
              <option value="alphanumeric">Alphanumeric</option>
              <option value="alpha">Alpha</option>
              <option value="numeric">Numeric</option>
            </Select>
            <Select label="Letter Case" value={voucherForm.letter_case} onChange={e => vf('letter_case', e.target.value)}>
              <option value="upper">Upper</option>
              <option value="lower">Lower</option>
              <option value="mixed">Mixed</option>
            </Select>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={voucherForm.increment} onChange={e => vf('increment', e.target.checked)} style={{ accentColor: 'var(--accent-cyan)' }} />
              Increment (kode berurutan, bukan acak)
            </label>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 8 }}>Status Voucher</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['pre_active','Pre-Active','var(--accent-blue)','rgba(59,130,246,0.1)'],['active','Active','var(--accent-green)','rgba(16,185,129,0.1)']].map(([val,label,color,bg]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '6px 14px', borderRadius: 'var(--radius)', border: `1px solid ${voucherForm.status === val ? color : 'var(--border)'}`, background: voucherForm.status === val ? bg : 'var(--bg-input)', color: voucherForm.status === val ? color : 'var(--text-secondary)', transition: 'all 0.15s', fontWeight: voucherForm.status === val ? 600 : 400 }}>
                  <input type="radio" name="voucher_status" value={val} checked={voucherForm.status === val} onChange={() => vf('status', val)} style={{ accentColor: 'currentColor' }} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-input)', borderRadius: 'var(--radius)', padding: '8px 12px' }}>
            Preview: <span style={{ color: 'var(--text-mono)', fontFamily: 'monospace' }}>
              {[voucherForm.prefix, '×'.repeat(voucherForm.length || 8)].filter(Boolean).join(voucherForm.separator || '')}
            </span>
          </div>

          {/* custom_attrs */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                RADIUS Attributes
                <span style={{ color: 'var(--accent-purple)', marginLeft: 6 }}>({voucherForm.custom_attrs.length})</span>
              </span>
              <Button size="sm" variant="ghost" onClick={addVoucherAttr}><Plus size={12} /> Tambah</Button>
            </div>
            {voucherForm.custom_attrs.length === 0 ? (
              <div style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px dashed var(--border)', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                Kosong — dikirim sebagai <span className="mono" style={{ color: 'var(--accent-purple)' }}>custom_attrs: []</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 58px 1fr 22px', gap: 6 }}>
                  {['Type','Attribute','Op','Value',''].map(h => (
                    <span key={h} style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{h}</span>
                  ))}
                </div>
                {voucherForm.custom_attrs.map((attr, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 58px 1fr 22px', gap: 6, alignItems: 'center' }}>
                    <select value={attr.type} onChange={e => updateVoucherAttr(i, 'type', e.target.value)}
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 6px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                      <option value="reply">reply</option>
                      <option value="check">check</option>
                    </select>
                    <input value={attr.attribute} onChange={e => updateVoucherAttr(i, 'attribute', e.target.value)}
                      placeholder="Mikrotik-Rate-Limit"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                    <select value={attr.op} onChange={e => updateVoucherAttr(i, 'op', e.target.value)}
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 4px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                      {[':=','=','+=','==','!=','>=','<='].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input value={attr.value} onChange={e => updateVoucherAttr(i, 'value', e.target.value)}
                      placeholder="10M/10M"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                    <button onClick={() => removeVoucherAttr(i)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setVoucherModal(false)}>Batal</Button>
          <Button onClick={handleGenerateVouchers} loading={voucherSaving}>Generate</Button>
        </div>
      </Modal>

      {/* Attrs Modal */}
      <Modal open={!!attrsModal} onClose={() => setAttrsModal(null)} title={`Attributes — ${attrsModal?.username}`} width={480}>
        {attrsModal && (() => {
          const attrs = Array.isArray(attrsModal.custom_attrs) ? attrsModal.custom_attrs : [];
          return attrs.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 24 }}>Tidak ada custom attributes</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 4, fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', padding: '0 4px', marginBottom: 4 }}>
                <span>Attribute</span><span>Op</span><span>Value</span>
              </div>
              {attrs.map((a, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 4, padding: '8px 10px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-mono)', fontFamily: 'monospace' }}>{a.attribute}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{a.op}</span>
                  <span style={{ color: 'var(--accent-cyan)', fontFamily: 'monospace' }}>{a.value}</span>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="ghost" onClick={() => setAttrsModal(null)}>Tutup</Button>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus User" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus user <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.username}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ User ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk User" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Hapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} user</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ User ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus Semua</Button>
        </div>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal open={bulkUpdateModal} onClose={() => setBulkUpdateModal(false)} title={`Bulk Update — ${selected.length} user`} width={480}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Update field di bawah untuk semua user yang dipilih. Field kosong/tidak diubah akan dilewati.
          <br/><span style={{ color: 'var(--accent-orange)' }}>✗ Username, Password, Full Name, Expired At tidak bisa di-bulk update.</span>
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Status" value={bulkUpdateForm.status}
              onChange={e => setBulkUpdateForm(f => ({ ...f, status: e.target.value }))}>
              <option value="">— Tidak diubah —</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </Select>
            <SearchSelect label="Profile" value={bulkUpdateForm.profile_id}
              onChange={id => setBulkUpdateForm(f => ({ ...f, profile_id: id || '' }))}
              options={profiles} getKey={p => p.id} getLabel={p => p.name}
              allowClear clearLabel="— Tidak diubah —" placeholder="— Tidak diubah —" defaultCount={10} />
            <Select label="IP Assignment" value={bulkUpdateForm.ip_assignment}
              onChange={e => setBulkUpdateForm(f => ({ ...f, ip_assignment: e.target.value }))}>
              <option value="">— Tidak diubah —</option>
              <option value="nas">NAS (dynamic)</option>
              <option value="static">Static IP</option>
            </Select>
            <Input label="Static IP" value={bulkUpdateForm.static_ip}
              onChange={e => setBulkUpdateForm(f => ({ ...f, static_ip: e.target.value }))}
              placeholder="kosong = tidak diubah" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setBulkUpdateModal(false)}>Batal</Button>
          <Button onClick={handleBulkUpdate} loading={bulkUpdating}>Update {selected.length} User</Button>
        </div>
      </Modal>
    </div>
  );
}